import CustomerContextSelector from '../taxcontext/CustomerContextSelector';
import useTaxCustomer from '../taxcontext/useTaxCustomer';
// TaxDashboard.js
import { useEffect, useMemo, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import {
	useGetKurangBayarQuery,
	useGetTaxYearSummaryQuery,
	useGetPayerSummaryQuery,
	useSimulateTaxScenarioMutation,
	useCompareTaxScenariosMutation,
} from './taxApiSlice';
import KurangBayarCard from './KurangBayarCard';
import TaxYearSummaryCard from './TaxYearSummaryCard';
import PayerSummaryTable from './PayerSummaryTable';

import PajakContextBar from '../taxcontext/PajakContextBar';
import { useGetTaxpayersQuery } from '../taxpayers/taxpayersApiSlice';
import { useSelector } from 'react-redux';
import {
	selectCurrentTaxpayerId,
	selectCurrentTaxYear,
} from '../taxcontext/taxContextSlice';

const TaxDashboard = () => {
	const { getAppAccess } = useAuth();
	const pajakAccess = getAppAccess('PAJAK_AUTOMATION');

	const customer_id = useTaxCustomer();
	const taxpayer_id = useSelector(selectCurrentTaxpayerId);
	const tax_year = useSelector(selectCurrentTaxYear);

	const [selectedScenarioId, setSelectedScenarioId] = useState('');
	const [simulationMode, setSimulationMode] = useState('single');
	const [simulationError, setSimulationError] = useState('');

	const canSearch =
		Number(customer_id) > 0 &&
		Number(taxpayer_id) > 0 &&
		Number(tax_year) > 0;

	const queryArgs = useMemo(() => {
		if (!canSearch) return null;

		return {
			customer_id: Number(customer_id),
			taxpayer_id: Number(taxpayer_id),
			tax_year: Number(tax_year),
		};
	}, [customer_id, taxpayer_id, tax_year, canSearch]);

	const {
		currentData: taxpayers,
		isFetching: isLoadingTaxpayers,
        error: taxpayerError,
	} = useGetTaxpayersQuery({ customer_id, limit: 200 }, { skip: !customer_id });

	const {
		currentData: kurangBayarData,
		isLoading: isKurangBayarLoading,
		isSuccess: isKurangBayarSuccess,
		isError: isKurangBayarError,
		error: kurangBayarError,
	} = useGetKurangBayarQuery(queryArgs, {
		skip: !queryArgs,
	});

	const {
		currentData: taxYearSummaryData,
		isLoading: isTaxYearSummaryLoading,
		isSuccess: isTaxYearSummarySuccess,
		isError: isTaxYearSummaryError,
		error: taxYearSummaryError,
	} = useGetTaxYearSummaryQuery(queryArgs, {
		skip: !queryArgs,
	});

	const {
		currentData: payerSummaryData,
		isLoading: isPayerSummaryLoading,
		isSuccess: isPayerSummarySuccess,
		isError: isPayerSummaryError,
		error: payerSummaryError,
	} = useGetPayerSummaryQuery(
		queryArgs
			? { ...queryArgs, sifat_pemotongan: 'TIDAK FINAL' }
			: null,
		{
			skip: !queryArgs,
		}
	);

	const [
		simulateTaxScenario,
		{
			data: simulationData,
			isLoading: isSimulating,
			reset: resetSimulationMutation,
		},
	] = useSimulateTaxScenarioMutation();

	const [
		compareTaxScenarios,
		{
			data: compareData,
			isLoading: isComparing,
			reset: resetCompareMutation,
		},
	] = useCompareTaxScenariosMutation();

    useEffect(() => {
      resetSimulationMutation();
      resetCompareMutation();
      setSimulationError('');
    }, [customer_id, taxpayer_id, tax_year, resetSimulationMutation, resetCompareMutation]);

	const loading =
		isKurangBayarLoading || isTaxYearSummaryLoading || isPayerSummaryLoading;

	const hasError =
		isKurangBayarError || isTaxYearSummaryError || isPayerSummaryError;

	const errorMessage =
		kurangBayarError?.data?.message ||
		taxYearSummaryError?.data?.message ||
		payerSummaryError?.data?.message ||
		'Failed to load tax data.';

	const scenarioOptions = [
		{ value: '', label: 'Pilih skenario PTKP' },
		{ value: '5', label: 'PTKP K/0' },
		{ value: '8', label: 'PTKP K/3' },
		{ value: '12', label: 'PTKP K/I/3' },
	];

	const currentPtkpStatus =
		kurangBayarData?.ptkp_status || taxYearSummaryData?.ptkp_status || '-';

	const isSimulationBusy = isSimulating || isComparing;

	const clearSimulationResults = () => {
		setSimulationError('');
		resetSimulationMutation();
		resetCompareMutation();
	};

	const handleRunSimulation = async () => {
		if (!canSearch || !selectedScenarioId) return;

		try {
			setSimulationMode('single');
			setSimulationError('');
			resetCompareMutation();

			await simulateTaxScenario({
                customer_id: Number(customer_id),
				taxpayer_id: Number(taxpayer_id),
				tax_year: Number(tax_year),
				tax_profile_scenarios_id: Number(selectedScenarioId),
			}).unwrap();
		} catch (err) {
			setSimulationError(
				err?.data?.message || err?.message || 'Gagal menjalankan simulasi.'
			);
		}
	};

	const handleCompareScenarios = async () => {
		if (!canSearch) return;

		try {
			setSimulationMode('compare');
			setSimulationError('');
			resetSimulationMutation();

			await compareTaxScenarios({
                customer_id: Number(customer_id),
				taxpayer_id: Number(taxpayer_id),
				tax_year: Number(tax_year),
				scenario_ids: [5, 8, 12],
			}).unwrap();
		} catch (err) {
			setSimulationError(
				err?.data?.message || err?.message || 'Gagal membandingkan skenario.'
			);
		}
	};

	return (
		<div className="space-y-6">
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <CustomerContextSelector />
                {taxpayerError && <p role="alert" className="mb-3 text-sm text-red-700">{taxpayerError?.data?.message || 'Gagal memuat taxpayer.'}</p>}
				<PajakContextBar
					taxpayers={taxpayers}
					isLoadingTaxpayers={isLoadingTaxpayers}
				/>
			</section>

			{canSearch && (
				<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
					<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
						<div>
							<h2 className="text-base font-semibold text-slate-800">
								Simulasi Pajak
							</h2>
							<p className="mt-1 text-sm text-slate-500">
								Uji dampak perubahan asumsi PTKP terhadap PPh terutang
								berdasarkan data faktual wajib pajak terpilih.
							</p>
						</div>

						<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
							PTKP aktual saat ini:{' '}
							<span className="font-semibold">{currentPtkpStatus}</span>
						</div>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_auto] lg:items-end">
						<div>
							<label
								htmlFor="selectedScenarioId"
								className="mb-2 block text-sm font-medium text-slate-700"
							>
								Skenario PTKP
							</label>
							<select
								id="selectedScenarioId"
								name="selectedScenarioId"
								value={selectedScenarioId}
								onChange={(e) => setSelectedScenarioId(e.target.value)}
								className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
							>
								{scenarioOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						<div className="flex flex-wrap gap-3">
							<button
								type="button"
								onClick={handleRunSimulation}
								disabled={!selectedScenarioId || isSimulationBusy}
								className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isSimulating ? 'Menjalankan...' : 'Jalankan Simulasi'}
							</button>

							<button
								type="button"
								onClick={handleCompareScenarios}
								disabled={isSimulationBusy}
								className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isComparing ? 'Membandingkan...' : 'Bandingkan Skenario'}
							</button>

							<button
								type="button"
								onClick={clearSimulationResults}
								className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
							>
								Reset
							</button>
						</div>
					</div>

					{simulationError && (
						<div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							{simulationError}
						</div>
					)}

					{simulationMode === 'single' && simulationData && (
						<div className="mt-4 space-y-4">
							<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
								<div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
										PTKP Aktual
									</div>
									<div className="mt-2 text-lg font-semibold text-slate-800">
										{simulationData?.actual_result?.ptkp_status || '-'}
									</div>
									<div className="mt-1 text-sm text-slate-500">
										Rp{' '}
										{Number(
											simulationData?.actual_result?.ptkp_amount || 0
										).toLocaleString('id-ID')}
									</div>
								</div>

								<div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-blue-600">
										Skenario
									</div>
									<div className="mt-2 text-lg font-semibold text-slate-800">
										{simulationData?.simulated_result?.ptkp_status || '-'}
									</div>
									<div className="mt-1 text-sm text-slate-500">
										{simulationData?.scenario?.scenario_name || '-'}
									</div>
								</div>

								<div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
										Selisih Pajak YTD
									</div>
									<div className="mt-2 text-lg font-semibold text-slate-800">
										Rp{' '}
										{Number(
											simulationData?.delta?.tax_due_ytd_diff || 0
										).toLocaleString('id-ID')}
									</div>
									<div className="mt-1 text-sm text-slate-500">
										Perbandingan aktual vs simulasi
									</div>
								</div>
							</div>

							<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<h3 className="text-sm font-semibold text-slate-800">
										Hasil Aktual
									</h3>
									<div className="mt-3 space-y-2 text-sm text-slate-600">
										<div className="flex justify-between gap-4">
											<span>PKP</span>
											<span className="font-medium text-slate-800">
												Rp{' '}
												{Number(
													simulationData?.actual_result?.pkp || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
										<div className="flex justify-between gap-4">
											<span>Tax Due YTD</span>
											<span className="font-medium text-slate-800">
												Rp{' '}
												{Number(
													simulationData?.actual_result?.tax_due_ytd || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
										<div className="flex justify-between gap-4">
											<span>Kurang Bayar YTD</span>
											<span className="font-medium text-rose-600">
												Rp{' '}
												{Number(
													simulationData?.actual_result?.kurang_bayar_ytd || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
									</div>
								</div>

								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<h3 className="text-sm font-semibold text-slate-800">
										Hasil Simulasi
									</h3>
									<div className="mt-3 space-y-2 text-sm text-slate-600">
										<div className="flex justify-between gap-4">
											<span>PKP</span>
											<span className="font-medium text-slate-800">
												Rp{' '}
												{Number(
													simulationData?.simulated_result?.pkp || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
										<div className="flex justify-between gap-4">
											<span>Tax Due YTD</span>
											<span className="font-medium text-slate-800">
												Rp{' '}
												{Number(
													simulationData?.simulated_result?.tax_due_ytd || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
										<div className="flex justify-between gap-4">
											<span>Kurang Bayar YTD</span>
											<span className="font-medium text-emerald-600">
												Rp{' '}
												{Number(
													simulationData?.simulated_result?.kurang_bayar_ytd || 0
												).toLocaleString('id-ID')}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{simulationMode === 'compare' && compareData?.data?.length > 0 && (
						<div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
							<table className="min-w-full divide-y divide-slate-200 text-sm">
								<thead className="bg-slate-50">
									<tr>
										<th className="px-4 py-3 text-left font-semibold text-slate-600">
											Skenario
										</th>
										<th className="px-4 py-3 text-left font-semibold text-slate-600">
											PTKP
										</th>
										<th className="px-4 py-3 text-right font-semibold text-slate-600">
											PKP
										</th>
										<th className="px-4 py-3 text-right font-semibold text-slate-600">
											Tax Due YTD
										</th>
										<th className="px-4 py-3 text-right font-semibold text-slate-600">
											Kurang Bayar YTD
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-100 bg-white">
									{compareData.data.map((item) => (
										<tr key={item?.scenario?.tax_profile_scenarios_id}>
											<td className="px-4 py-3 text-slate-700">
												{item?.scenario?.scenario_name || '-'}
											</td>
											<td className="px-4 py-3 text-slate-700">
												{item?.simulated_result?.ptkp_status || '-'}
											</td>
											<td className="px-4 py-3 text-right text-slate-700">
												Rp{' '}
												{Number(
													item?.simulated_result?.pkp || 0
												).toLocaleString('id-ID')}
											</td>
											<td className="px-4 py-3 text-right text-slate-700">
												Rp{' '}
												{Number(
													item?.simulated_result?.tax_due_ytd || 0
												).toLocaleString('id-ID')}
											</td>
											<td className="px-4 py-3 text-right font-medium text-slate-800">
												Rp{' '}
												{Number(
													item?.simulated_result?.kurang_bayar_ytd || 0
												).toLocaleString('id-ID')}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>
			)}

			{!canSearch && (
				<section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
					Fill taxpayer and tax year first to display the tax summary.
				</section>
			)}

			{loading && (
				<section className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
					Loading tax data...
				</section>
			)}

			{hasError && (
				<section className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700 shadow-sm">
					{errorMessage}
				</section>
			)}

			{!loading && !hasError && canSearch && (
				<>
					{isKurangBayarSuccess && <KurangBayarCard data={kurangBayarData} />}
					{isTaxYearSummarySuccess && (
						<TaxYearSummaryCard data={taxYearSummaryData} />
					)}
					{isPayerSummarySuccess && (
						<PayerSummaryTable data={payerSummaryData} />
					)}
				</>
			)}
		</div>
	);
};

export default TaxDashboard;